<template>
    <Layout class="customers-members-page" is-crm>
        <header class="page__top">
            <h1 v-t="'crm.customers.members.title'"></h1>
            <div class="customers-members-page__toolbar">
                <WithGlobalPermission permission="Invite.list">
                    <div>
                        <router-link class="link" :to="invitesRoute">Invites</router-link>
                    </div>
                </WithGlobalPermission>
                <WithGlobalPermission permission="CustomerMember.create_users">
                    <router-link
                        :to="{ name: 'customers-members-create' }"
                        type="button"
                        class="button button--big customers-members-page__create-batch"
                    >
                        <IconUser />
                        <span v-text="$t('crm.customers.members.add.trigger')" />
                    </router-link>
                </WithGlobalPermission>
            </div>
        </header>
        <PaginatedResource
            ref="customersMembersResource"
            :fetch-method="fetchCustomersMembers"
            :filter-fields="filterKeys"
            :sorting-fields="sortingFields"
            delay-fetch
        >
            <template
                #default="{
                    loading,
                    sort,
                    search,
                    filter,
                    canLoadMore,
                    resource: users,
                    isEmpty,
                    notFound,
                    filters,
                    loadMore,
                }"
            >
                <div class="customers-members">
                    <Spinner v-if="loading" />
                    <UsersFilters
                        class="customers-members-page__filters"
                        :filters="filters"
                        :customers="customers"
                        :roles="roles"
                        @search="search"
                        @filter="filter"
                    />
                    <UsersTable
                        v-if="!isEmpty"
                        class="customers-members-page__table"
                        :users="users"
                        :sorting-fields="sortingFields"
                        @sort="sort"
                    >
                        <template #head-row-append>
                            <th
                                v-if="isAdmin"
                                style="width: 15%; max-width: 150px"
                                class="control-table__cell"
                            >
                                <div class="control-table__cell-inner users-table__cell">
                                    {{ $t('crm.users.list.customer') }}
                                </div>
                            </th>
                        </template>
                        <template #item-row-append="{ user }">
                            <td
                                v-if="isAdmin"
                                class="control-table__cell users-table__cell users-table__cell--left"
                            >
                                <router-link
                                    :to="{
                                        name: 'customers-id',
                                        params: { id: user.customer },
                                    }"
                                >
                                    {{ (customersById[user.customer] || {}).name || 'N/A' }}
                                </router-link>
                            </td>
                        </template>
                        <template #item-actions="{ user }">
                            <UsersEdit
                                v-if="user.permissions.update"
                                :value="user"
                                class="user-action"
                                :schema="schema.partial_update"
                                :customers="customers"
                                :languages="languagesArray"
                                modal-title="crm.customers.members.edit.title"
                                @save="updateUser"
                            />
                            <UsersDelete
                                v-if="user.permissions.destroy"
                                class="user-action"
                                @confirmed="deleteUser(user.id)"
                            />
                            <TokensCreate
                                v-if="user.permissions.token"
                                :schema="schema.token"
                                :customer-member-id="user.id"
                            >
                                <template #trigger="{ open }">
                                    <button
                                        class="button button--icon button--darkgray"
                                        @click.prevent="open"
                                    >
                                        <IconKey class="button__icon" />
                                        <span v-t="'misc.token'" />
                                    </button>
                                </template>
                            </TokensCreate>
                        </template>
                    </UsersTable>

                    <div v-if="isEmpty" class="customers-members-page__empty">
                        {{ $t('crm.customers.members.list.empty') }}
                    </div>
                    <div v-else-if="notFound" class="customers-members-page__empty">
                        {{ $t('crm.customers.members.list.notFound') }}
                    </div>
                    <div v-if="canLoadMore" class="customers-members__loadmore">
                        <button
                            class="button button--big button--blank"
                            type="button"
                            @click="loadMore"
                        >
                            {{ $t('misc.loadmore') }}
                        </button>
                    </div>
                </div>
            </template>
        </PaginatedResource>
    </Layout>
</template>

<script>
import {
    fetchCustomersMembers,
    updateCustomersMember,
    deleteCustomersMember,
    fetchCustomersMembersLanguages,
} from '../../api/customers';
import { UsersDelete, UsersEdit, UsersFilters, UsersTable } from '../../../users/components';
import { TokensCreate } from '../../../tokens/components';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { IconUser, IconKey } from '@speechki-frontend/shared/ui/icons';
import { WithGlobalPermission } from '@speechki-frontend/core/auth/components';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
    name: 'CustomersMembersPage',
    components: {
        Layout,
        Spinner,
        IconUser,
        IconKey,
        PaginatedResource,
        UsersDelete,
        UsersEdit,
        UsersFilters,
        UsersTable,
        TokensCreate,
        WithGlobalPermission,
    },
    data() {
        return {
            filterKeys: ['role', 'customer_id'],
            sortingFields: ['id', 'email'],
            languages: {},
            customers: [],
        };
    },
    computed: {
        ...mapGetters(['isAdmin']),
        ...mapState('customersMember', ['schema']),
        ...mapGetters('workspaces', ['workspaceCustomerId']),
        roles() {
            if (!this.schema.create) return [];
            const { role } = this.schema.create;
            const { choices = [] } = role.child;
            return choices;
        },
        languagesArray() {
            return Object.entries(this.languages).map(([id, label]) => ({ id, label }));
        },
        invitesRoute() {
            const id = this.workspaceCustomerId || 'speechki';

            return { name: 'customers-id-invites', params: { id } };
        },
        customersById() {
            return this.customers.reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {});
        },
    },
    watch: {
        workspaceCustomerId(customer) {
            this.$refs.customersMembersResource.filter({ customer_id: customer });
        },
    },
    async created() {
        try {
            this.customers = this.$store.state.workspaces.workspaces;
            await Promise.all([this.fetchSchema(), this.fetchLanguages()]);
        } catch (err) {
            console.log(err);
        }
    },
    mounted() {
        this.refetch();
    },
    methods: {
        fetchCustomersMembers,
        ...mapActions(['pushToast']),
        ...mapActions('customer', ['fetchCustomers']),
        ...mapActions('customersMember', ['fetchSchema']),

        refetch() {
            this.$refs.customersMembersResource.filter({ customer_id: this.workspaceCustomerId });
        },

        async fetchLanguages() {
            this.loading = true;

            try {
                this.languages = await fetchCustomersMembersLanguages();
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },

        async updateUser({ id, languages, role }) {
            try {
                await updateCustomersMember(id, { languages, role });
                this.refetch();
                this.pushToast({
                    id: Date.now(),
                    type: 'success',
                    title: 'Saved',
                });
            } catch (error) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    text: error,
                });
                console.log(error);
            }
        },
        async deleteUser(id) {
            try {
                await deleteCustomersMember(id);
                this.refetch();
                this.pushToast({
                    id: Date.now(),
                    type: 'success',
                    title: 'Deleted',
                });
            } catch (error) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    text: error,
                });
                console.log(error);
            }
        },
    },
};
</script>

<style lang="scss" src="./CustomersMembersPage.scss"></style>
